import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Sort } from '@angular/material/sort';

import { toCanvas } from 'html-to-image';

import { Constants } from 'app/constants/constants';
import { FieldService } from 'app/providers/storage-api/field.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { decimalsToPipeDigitInfo } from '../widget-predictive/predictive.utils';
import { WidgetPredictiveOptions } from '../widget-predictive/widget-predictive.component';
import { IExportCanvas } from '../../models/export-canvas';
import { Breakdown, Driver } from '../../models/predictive.models';

interface DriverColumnGroupDef {
  name: string;
  label: string;
}

class CONSTANTS {
  static readonly CURRENT_COLUMN = 'current';
  static readonly CHANGE_COLUMN = 'change';
  static readonly SCORE_COLUMN = 'score';
  static readonly RANK_COLUMN = 'rank';
  static readonly BREAKDOWN_COLUMN = 'breakdown';
  static readonly DEPENDENT_COLUMN = 'dependent';
  static readonly PERCENTAGE_COLUMN = 'percentage';
  static readonly BASE_GROUP_COLUMN = 'base';
  static readonly SEPARATOR = '-';
}

@EnableClassTelemetry()
@Component({
  selector: 'app-predictive-crosstab',
  templateUrl: './predictive-crosstab.component.html',
  styleUrls: ['./predictive-crosstab.component.scss'],
  standalone: false,
})
export class PredictiveCrosstabComponent implements OnInit, OnChanges, IExportCanvas {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() breakdowns: Breakdown[];
  @Input() showChange: boolean;
  @Input() dependentColumn: string;
  @Input() changeColumnLabel: string;
  @Input() predictiveOptions!: WidgetPredictiveOptions;

  dependentColumnLabel: string;

  @ViewChild('table', { read: ElementRef }) table: ElementRef;

  dataSource = [];
  sort: Sort = {
    active: 'breakdown',
    direction: 'asc',
  };
  groupColumns: string[];
  displayedColumns: string[];
  driverColumnGroupDefs: DriverColumnGroupDef[];
  colSpan: number;
  lastDriverSubColumn: string;
  numberPipeDigitInfo: string;

  isCurrentScoreChecked = true;
  isChangeChecked = false;
  isImportanceChecked = true;
  isImportanceToggled = false;

  readonly CONSTANTS = CONSTANTS;

  constructor() {}

  ngOnInit(): void {
    if (this.showChange) {
      this.isChangeChecked = true;
    }

    this.dependentColumnLabel = FieldService.getFieldLongName(this.dependentColumn);

    this.numberPipeDigitInfo = decimalsToPipeDigitInfo(this.predictiveOptions.decimals);

    this.setColumns();

    this.setDataSource();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showChange && !changes.showChange.firstChange) {
      this.isChangeChecked = changes.showChange.currentValue;

      this.setColumns();
    }
  }

  onSortChanged(sort: Sort): void {
    this.sort = sort;

    this.setDataSource();
  }

  isLastDriverSubColumn(column: string): boolean {
    return column === this.lastDriverSubColumn;
  }

  formatColumnDefName(column: string, driverName: string): string {
    return `${column}${this.CONSTANTS.SEPARATOR}${driverName}`;
  }

  getDriver(breakdown: Breakdown, driverName: string): Driver {
    return breakdown.drivers.find((d) => d.name === driverName);
  }

  refresh(): void {
    this.setColumns();
  }

  exportCanvas(): Promise<HTMLCanvasElement> {
    return toCanvas(this.table.nativeElement);
  }

  private setColumns(): void {
    this.groupColumns = [this.CONSTANTS.BASE_GROUP_COLUMN];
    this.displayedColumns = [
      this.CONSTANTS.BREAKDOWN_COLUMN,
      this.CONSTANTS.DEPENDENT_COLUMN,
      this.CONSTANTS.PERCENTAGE_COLUMN,
    ];
    this.driverColumnGroupDefs = [];

    if (this.breakdowns.length) {
      const subColumns = this.getSubColumns();

      if (!subColumns.length) {
        return;
      }

      this.colSpan = subColumns.length;

      this.breakdowns[0].drivers.forEach((d) => {
        this.groupColumns.push(d.name);

        this.driverColumnGroupDefs.push({
          name: d.name,
          label: d.fieldLongName,
        });

        subColumns.forEach((c) => this.displayedColumns.push(this.formatColumnDefName(c, d.name)));
      });

      this.lastDriverSubColumn = subColumns.pop();
    }
  }

  private getSubColumns(): string[] {
    const result: string[] = [];

    if (this.isCurrentScoreChecked) {
      result.push(this.CONSTANTS.CURRENT_COLUMN);
    }

    if (this.isChangeChecked) {
      result.push(this.CONSTANTS.CHANGE_COLUMN);
    }

    if (this.isImportanceChecked) {
      result.push(
        this.isImportanceToggled ? this.CONSTANTS.RANK_COLUMN : this.CONSTANTS.SCORE_COLUMN
      );
    }

    return result;
  }

  private setDataSource(): void {
    const columnDefInfo = this.sort.active.split(this.CONSTANTS.SEPARATOR);
    const column = columnDefInfo[0];

    const sorting = (a: Breakdown, b: Breakdown): number => {
      if (a.name === Constants.TWTRA_ALL_BREAKDOWN) {
        return -1;
      } else if (b.name === Constants.TWTRA_ALL_BREAKDOWN) {
        return 1;
      }

      let result = 0;
      switch (column) {
        case this.CONSTANTS.BREAKDOWN_COLUMN:
          result = a.name.localeCompare(b.name);
          break;
        case this.CONSTANTS.DEPENDENT_COLUMN:
          result = a.value - b.value;
          break;
        case this.CONSTANTS.PERCENTAGE_COLUMN:
          result = a.weight - b.weight;
          break;
        case this.CONSTANTS.CURRENT_COLUMN:
          result =
            this.getDriver(a, columnDefInfo[1]).current -
            this.getDriver(b, columnDefInfo[1]).current;
          break;
        case this.CONSTANTS.CHANGE_COLUMN:
          {
            const driverA = this.getDriver(a, columnDefInfo[1]);
            const driverB = this.getDriver(b, columnDefInfo[1]);

            result = driverA.current - driverA.trend - (driverB.current - driverB.trend);
          }
          break;
        case this.CONSTANTS.SCORE_COLUMN:
          result =
            this.getDriver(a, columnDefInfo[1]).importancePopulator -
            this.getDriver(b, columnDefInfo[1]).importancePopulator;
          break;
        case this.CONSTANTS.RANK_COLUMN:
          result =
            this.getDriver(a, columnDefInfo[1]).importanceCounter -
            this.getDriver(b, columnDefInfo[1]).importanceCounter;
          break;
      }

      const direction = this.sort.direction === 'asc' ? 1 : -1;
      return result * direction;
    };

    this.dataSource = [...this.breakdowns.sort(sorting)];
  }
}
